import {
  formatMerchantDate,
  healthBadge,
  type MerchantBadgeView,
} from "../view-model-formatters.js";
import type { MerchantNotification, NotificationEventType, NotificationStatus } from "./models.js";

export function notificationStatusBadge(status: NotificationStatus): MerchantBadgeView {
  const tones: Record<NotificationStatus, MerchantBadgeView["tone"]> = {
    PENDING: "orange",
    SENT: "green",
    READ: "gray",
    FAILED: "red",
  };
  return healthBadge(tones[status], titleCase(status));
}

export function notificationEventLabel(eventType: NotificationEventType): string {
  const labels: Record<NotificationEventType, string> = {
    INTEGRATION_ISSUE: "Integration issue",
    MERCHANT_USER_PROVISIONED: "New store user",
    MONITORING_DIGEST: "Monitoring digest",
    MONITORING_FAILED: "Monitoring issue",
    ORDER_ATTRIBUTED: "Order attribution",
    PRICE_CHANGED: "Price change",
    REFUND_ATTRIBUTED: "Refund attribution",
    REQUEST_EXPIRING_SOON: "Offer or review deadline",
    REQUEST_CUSTOMER_MESSAGE: "Customer message",
    REQUEST_MANUAL_REVIEW: "Request needs review",
    REVIEW_REQUIRED: "Review required",
    STOCK_CHANGED: "Stock change",
  };
  return labels[eventType];
}

export function notificationEventDescription(eventType: NotificationEventType): string {
  const descriptions: Record<NotificationEventType, string> = {
    INTEGRATION_ISSUE:
      "Use this when Price Rescue needs you to reconnect the platform, restore required permissions, fix webhook delivery, or restart catalog sync before automation can continue.",
    MERCHANT_USER_PROVISIONED:
      "A store user accessed Price Rescue and received an initial app role.",
    MONITORING_DIGEST: "A summary of competitor monitoring updates.",
    MONITORING_FAILED:
      "Use this when competitor monitoring, product mapping checks, or catalog checks fail and need attention before Price Rescue can make reliable decisions.",
    ORDER_ATTRIBUTED: "An order was attributed to a Price Rescue offer.",
    PRICE_CHANGED: "A competitor price changed.",
    REFUND_ATTRIBUTED: "A refund adjusted offer attribution.",
    REQUEST_EXPIRING_SOON:
      "Use this when an offer is close to expiry or a price-match request is approaching its merchant review deadline. The review deadline does not expire the request.",
    REQUEST_CUSTOMER_MESSAGE: "A customer added a message to a price-match request conversation.",
    REQUEST_MANUAL_REVIEW: "A price-match request entered the merchant manual-review queue.",
    REVIEW_REQUIRED:
      "Use this when a customer price-match request cannot be decided automatically and needs a merchant user to review evidence, pricing, and the recommended action.",
    STOCK_CHANGED: "A competitor stock state changed.",
  };
  return descriptions[eventType];
}

export function notificationTimestampLabel(notification: MerchantNotification): string {
  return formatMerchantDate(notification.readAt ?? notification.createdAt);
}

function titleCase(value: string): string {
  return value
    .toLowerCase()
    .replace(
      /(^|_)([a-z])/g,
      (_match, separator: string, letter: string) =>
        `${separator === "_" ? " " : ""}${letter.toUpperCase()}`,
    );
}
